import { CLASS_SLOT_LABELS, WEEKDAY_LABELS, type ClassSection, type PublicClassSection } from "@/api/types/classRegistration";

/**
 * Class Registration Engine (Phase 2c) display helpers — pure,
 * display-only functions mirroring features/memberships/hourMath.ts's
 * posture: nothing here computes a number the API is the source of truth
 * for (cap/min_enrollment/below_minimum/seats_remaining are all
 * server-derived).
 */

/** "Monday · 9:00–11:00 AM" */
export function formatWeekdaySlot(weekday: ClassSection["weekday"], slot: ClassSection["slot"]): string {
  return `${WEEKDAY_LABELS[weekday]} · ${CLASS_SLOT_LABELS[slot]}`;
}

/** "8 / 12" enrolled-vs-cap for the sections table. */
export function formatCapacity(activeCount: number, cap: number): string {
  return `${activeCount} / ${cap}`;
}

/** A section's display name for a row/header when it has no program
 * (the lunch extension) — never falls back to a raw kind string. */
export function sectionDisplayName(section: Pick<ClassSection | PublicClassSection, "program_name" | "kind">): string {
  if (section.program_name) return section.program_name;
  return section.kind === "lunch_extension" ? "Lunch Extension" : "Class";
}

/** "3 seats left" / "Waitlist only" for the public registration picker. */
export function formatSeatsRemaining(seatsRemaining: number, waitlistOpen: boolean): string {
  if (seatsRemaining > 0) return `${seatsRemaining} seat${seatsRemaining === 1 ? "" : "s"} left`;
  return waitlistOpen ? "Waitlist only" : "Full";
}

/** Clear date + time for the compliance consent-audit trail — never a raw
 * ISO string (this is a compliance-facing view, not a debug log). */
export function formatConsentTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "Mar 3, 2020" — plain calendar date (child birthdate), no time-of-day
 * component since child_birthdate is a bare "YYYY-MM-DD" date string.
 * Pure string parsing to avoid the UTC-midnight-shifts-a-day-back
 * timezone pitfall (same discipline as
 * features/memberships/hourMath.ts::formatShortDate). */
export function formatBirthdate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${MONTH_ABBR[month - 1]} ${day}, ${year}`;
}
